import { useState, useEffect } from 'react';
import { couponsService } from '@/shared/services/couponsService';
import { Coupon, CouponStatus } from '@/shared/types/coupons';
import styles from './CouponsAdminPage.module.css';

export const CouponsAdminPage = () => {
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<CouponStatus | ''>('');
    const [restaurantIdFilter, setRestaurantIdFilter] = useState('');

    useEffect(() => {
        fetchCoupons();
    }, [statusFilter]);

    const fetchCoupons = async () => {
        setLoading(true);
        try {
            const response = await couponsService.listAll({
                status: statusFilter || undefined,
                restaurantId: restaurantIdFilter || undefined
            });
            setCoupons(response.coupons);
        } catch (error) {
            console.error('Error fetching coupons:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDisable = async (couponId: string) => {
        if (!confirm('¿Estás seguro de que deseas desactivar este cupón de forma permanente?')) return;

        try {
            await couponsService.disable(couponId);
            alert('Cupón desactivado correctamente.');
            fetchCoupons();
        } catch (error) {
            console.error('Error disabling coupon:', error);
            alert('Error al desactivar el cupón.');
        }
    };

    const formatDate = (timestamp: { seconds: number }) => {
        return new Date(timestamp.seconds * 1000).toLocaleDateString();
    };

    const getStatusClass = (status: CouponStatus) => {
        switch (status) {
            case 'active': return styles.statusActive;
            case 'inactive': return styles.statusInactive;
            case 'expired': return styles.statusExpired;
            default: return '';
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Supervisión de Cupones</h1>
            </div>

            <div className={styles.filters}>
                <div className={styles.filterGroup}>
                    <label>Estado</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as CouponStatus | '')}>
                        <option value="">Todos</option>
                        <option value="active">Activos</option>
                        <option value="inactive">Inactivos</option>
                        <option value="expired">Expirados</option>
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label>ID Comercio</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                            type="text"
                            placeholder="Buscar por ID..."
                            value={restaurantIdFilter}
                            onChange={(e) => setRestaurantIdFilter(e.target.value)}
                        />
                        <button className={styles.actionButton} onClick={fetchCoupons} style={{ padding: '0.5rem', cursor: 'pointer' }}> Buscar </button>
                    </div>
                </div>
            </div>

            <div className={styles.tableContainer}>
                {loading ? (
                    <div className={styles.emptyState}>Cargando cupones...</div>
                ) : coupons.length === 0 ? (
                    <div className={styles.emptyState}>No se encontraron cupones.</div>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Código</th>
                                <th>Tipo</th>
                                <th>Valor</th>
                                <th>Estado</th>
                                <th>Validez</th>
                                <th>Usos</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {coupons.map((coupon) => (
                                <tr key={coupon.couponId}>
                                    <td>
                                        <span className={styles.couponCode}>{coupon.code}</span>
                                    </td>
                                    <td>{coupon.type}</td>
                                    <td>
                                        {coupon.type === 'PERCENTAGE' ? `${coupon.value}%` : `$${coupon.value}`}
                                    </td>
                                    <td>
                                        <span className={`${styles.status} ${getStatusClass(coupon.status)}`}>
                                            {coupon.status === 'active' ? 'Activo' : coupon.status === 'inactive' ? 'Inactivo' : 'Expirado'}
                                        </span>
                                    </td>
                                    <td>
                                        {formatDate(coupon.validFrom)} - {formatDate(coupon.validUntil)}
                                    </td>
                                    <td>{coupon.usageCount} / {coupon.usageLimit || '∞'}</td>
                                    <td className={styles.actions}>
                                        <button
                                            className={styles.disableButton}
                                            onClick={() => handleDisable(coupon.couponId)}
                                            disabled={coupon.status !== 'active'}
                                        >
                                            Desactivar
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};
